// Bulk import/export configs per module path. Field names + endpoints match the
// EXISTING shared backend contract exactly (verified against the live templates,
// preview responses and commit schemas). No web-only formats or IDs are introduced.

export const IMPORTS = {
  "/ingredients": {
    title: "Import Ingredients",
    templateEndpoint: "/ingredients/template",
    previewEndpoint: "/ingredients/import/preview",
    commitEndpoint: "/ingredients/import/commit",
    logsEndpoint: "/ingredients/import-logs",
    duplicatesEndpoint: "/ingredients/duplicates",
    mergeEndpoint: "/ingredients/merge",
    rowsKey: "rows",
    accept: ".xlsx",
    confirmText:
      "This will update Ingredient Master data only. No purchase transaction, GRN, stock movement or AP will be created.",
    successText: "Ingredient Master updated. No stock or purchase transaction was created.",
    supportsDuplicateResolution: true,
    templateNote:
      "REQUIRED columns: Name, Standard Unit, Packing Size, Price/Pack. Leave Ingredient ID blank to create; fill an existing ING- code to update. Allowed units: kg, g, litre, ml, pcs, tray, batch.",
    previewColumns: [
      { key: "name", label: "Name" },
      { key: "category", label: "Category" },
      { key: "unit", label: "Unit" },
      { key: "packingSize", label: "Pack Size", align: "right" },
      { key: "price", label: "Price/Pack", align: "right", fmt: "currency" },
      { key: "costPerUnit", label: "Cost/Unit", align: "right", fmt: "currency" },
    ],
    buildCommitBody: (rows) => ({
      updateExisting: true,
      rows: rows
        .filter((r) => r.status !== "error")
        .map((r) => {
          const useExisting = r.status === "update" || (r.status === "duplicate" && r._decision !== "new");
          return {
            name: r.name,
            unit: r.unit,
            packingSize: Number(r.packingSize) || 0,
            packingUnit: r.packingUnit || r.unit || "kg",
            brand: r.brand || "",
            vendor: r.vendor || "",
            price: Number(r.price) || 0,
            category: r.category || "",
            gst: Number(r.gst) || 0,
            reorderLevel: Number(r.reorderLevel) || 0,
            minStock: Number(r.minStock) || 0,
            maxStock: Number(r.maxStock) || 0,
            status: useExisting ? "update" : "valid",
            existingId: useExisting ? r.existingId || "" : "",
            ingredientId: r.ingredientId || "",
          };
        }),
    }),
  },
  "/raw-material-inventory": {
    title: "Import Opening Stock (Raw Material)",
    templateEndpoint: "/inventory/opening-stock/template",
    previewEndpoint: "/inventory/opening-stock/preview",
    commitEndpoint: "/inventory/opening-stock/commit",
    rowsKey: "rows",
    accept: ".xlsx",
    confirmText:
      "This adds an OPENING receipt to the raw-material stock ledger only. Existing stock is NOT overwritten. No purchase, GRN or production transaction is created.",
    successText: "Opening-stock ledger entries created. Existing stock was not overwritten; no purchase/GRN transaction was made.",
    supportsDuplicateResolution: false,
    templateNote:
      "Columns: Ingredient ID, Ingredient Name, Quantity, Unit, Batch/Lot, Expiry, Supplier, Invoice/Reference, Remarks. The template is pre-filled with your ingredient master — match strictly by Ingredient ID (unknown IDs are rejected; ingredients are never auto-created). Just enter Quantity.",
    previewColumns: [
      { key: "ingredientId", label: "Ingredient ID" },
      { key: "name", label: "Ingredient" },
      { key: "qty", label: "Quantity", align: "right" },
      { key: "unit", label: "Unit" },
      { key: "batch", label: "Batch/Lot" },
      { key: "expiry", label: "Expiry" },
    ],
    buildCommitBody: (rows) => ({
      filename: "opening_stock",
      rows: rows
        .filter((r) => r.status !== "error")
        .map((r) => ({ ingredientId: r.ingredientId, ingredientKey: r.ingredientKey, name: r.name,
                       qty: Number(r.qty) || 0, batch: r.batch || "", expiry: r.expiry || "",
                       reference: r.reference || "", remarks: r.remarks || "", status: r.status })),
    }),
  },
};

// Recipe bulk import (rendered from the Recipes page). Imported recipes are saved
// as DRAFT by the backend — nothing auto-activates.
export const RECIPE_IMPORT = {
  title: "Import Recipes",
  templateEndpoint: "/recipes/template",
  previewEndpoint: "/recipes/import/preview",
  commitEndpoint: "/recipes/import/commit",
  logsEndpoint: "/recipes/import-logs",
  rowsKey: "recipes",
  accept: ".xlsx",
  confirmText:
    "Imported recipes are saved as DRAFT for approval — nothing goes live automatically. Ingredient prices are taken live from the Ingredient Master. No production or stock transaction is created.",
  successText: "Recipes imported as drafts. Submit each for approval when ready — nothing was activated automatically.",
  supportsDuplicateResolution: false,
  templateNote:
    "'Recipes' sheet: leave Recipe Number blank for a new recipe. 'Ingredients' sheet: Ingredient ID MUST be an existing ING- code (unknown IDs are rejected; ingredients are never auto-created). Prices come live from the Ingredient Master; Water always costs 0.",
  previewColumns: [
    { key: "recipeNumber", label: "Recipe #" },
    { key: "productName", label: "Product" },
    { key: "category", label: "Category" },
    { key: "yieldQty", label: "Yield", align: "right" },
    { key: "yieldUnit", label: "Unit" },
  ],
  buildCommitBody: (rows) => ({
    filename: "recipe_import",
    recipes: rows
      .filter((r) => r.status !== "error")
      .map((r) => ({
        recipeNumber: r.recipeNumber || "",
        productName: r.productName,
        category: r.category || "Bakery",
        yieldQty: Number(r.yieldQty) || 1,
        yieldUnit: r.yieldUnit || "pcs",
        status: r.status,
        ingredients: (r.ingredients || []).map((l) => ({
          ingredientId: l.ingredientId,
          ingredientCode: l.ingredientCode,
          name: l.name,
          quantity: Number(l.quantity) || 0,
          unit: l.unit,
          rate: Number(l.rate) || 0,
          rateUnit: l.rateUnit || l.unit,
        })),
      })),
  }),
};
export const FG_OPENING_IMPORT = {
  title: "Import Opening Stock (Finished Goods)",
  templateEndpoint: "/finished-goods/opening-stock/template",
  previewEndpoint: "/finished-goods/opening-stock/preview",
  commitEndpoint: "/finished-goods/opening-stock/commit",
  rowsKey: "rows",
  accept: ".xlsx",
  confirmText:
    "This adds an OPENING entry to the Finished Goods ledger only. Existing FG stock is NOT overwritten. No production, dispatch or sales transaction is created.",
  successText: "FG opening-stock ledger entries created. Existing stock was not overwritten.",
  supportsDuplicateResolution: false,
  templateNote:
    "Columns: Product ID, Product Name, Quantity, Batch ID, MFG Date, Expiry Date, Stock Status, Remarks. The template is pre-filled with your product master — match strictly by Product ID (unknown IDs rejected). Leave Batch ID blank for 'BATCH NOT RECORDED' (a batch is never invented). Just enter Quantity.",
  previewColumns: [
    { key: "productId", label: "Product ID" },
    { key: "productName", label: "Product" },
    { key: "qty", label: "Quantity", align: "right" },
    { key: "batchNo", label: "Batch" },
    { key: "mfgDate", label: "MFG" },
    { key: "expiryDate", label: "Expiry" },
  ],
  buildCommitBody: (rows) => ({
    rows: rows
      .filter((r) => r.status !== "error")
      .map((r) => ({
        productId: r.productId, sku: r.sku, productName: r.productName, unit: r.unit,
        qty: Number(r.qty) || 0, batchNo: r.batchNo || "",
        mfgDate: r.mfgDate || "", expiryDate: r.expiryDate || "",
        status: r.status,
      })),
  }),
};
